import { Injectable, inject } from '@angular/core';
import { Observable, lastValueFrom, map } from 'rxjs';
import { API_CONFIG, HttpService } from '@nebula-shared/data-access';

export interface ImportarAsientoDatosCabecera {
  terceroId?: number | null;
  terceroNombre?: string;
  numeroDocumento?: string;
  conceptoContableId?: number | null;
  tipoAsientoId?: number | null;
  fecha?: string;
}

export interface ImportarAsientoRequest {
  tipoArchivo: 'CSV' | 'XLSX' | 'XLS' | 'TXT';
  conEncabezado: boolean;
  archivoBase64: string;
}

export interface ImportFlatFileContent {
  lineas?: unknown[];
  errores?: string[];
}

export interface ImportFlatFileResult {
  lineas: unknown[];
  errores: string[];
}

export interface ImportarAsientoResponseApi {
  success?: boolean;
  message?: string;
  data?: unknown;
  content?: ImportFlatFileContent | unknown[];
}

@Injectable({ providedIn: 'root' })
export class ImportarAsientoService {
  private readonly http = inject(HttpService);
  private readonly causacionesBaseUrl: string;
  private readonly asientosBaseUrl: string;

  constructor() {
    const config = API_CONFIG.accounting;
    this.causacionesBaseUrl = `${config.baseUrl}/causaciones`;
    this.asientosBaseUrl = `${config.baseUrl}${config.endpoints.asientosContables}`;
  }

  importarAsiento(request: ImportarAsientoRequest): Observable<ImportarAsientoResponseApi> {
    return this.http
      .post<any>(`${this.causacionesBaseUrl}/importar-asiento`, request)
      .pipe(map((response: any) => this.normalizeResponse(response)));
  }

  async convertirArchivoABase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Error al convertir el archivo a Base64'));
      reader.readAsDataURL(file);
    });
  }

  async enviarArchivo(
    file: File,
    tieneEncabezado: boolean,
    datosCabecera?: ImportarAsientoDatosCabecera,
  ): Promise<ImportarAsientoResponseApi> {
    if (
      datosCabecera?.terceroId &&
      datosCabecera?.conceptoContableId &&
      datosCabecera?.tipoAsientoId
    ) {
      return this.enviarArchivoFlatFile(file, tieneEncabezado, datosCabecera);
    }

    const tipoArchivo = this.resolveTipoArchivo(file);
    const archivoBase64 = await this.convertirArchivoABase64(file);

    const requestBody: ImportarAsientoRequest = {
      tipoArchivo,
      conEncabezado: tieneEncabezado,
      archivoBase64,
    };

    return await lastValueFrom(this.importarAsiento(requestBody));
  }

  private async enviarArchivoFlatFile(
    file: File,
    tieneEncabezado: boolean,
    datosCabecera: ImportarAsientoDatosCabecera,
  ): Promise<ImportarAsientoResponseApi> {
    const tipoArchivo = this.resolveTipoArchivo(file);
    const formData = new FormData();

    formData.append('archivo', file);
    formData.append('tipoArchivo', tipoArchivo);
    formData.append('conEncabezado', String(tieneEncabezado));
    formData.append('terceroId', String(datosCabecera.terceroId));
    formData.append('conceptoContableId', String(datosCabecera.conceptoContableId));
    formData.append('tipoAsientoId', String(datosCabecera.tipoAsientoId));

    if (!datosCabecera.fecha) {
      throw new Error('La fecha del asiento es obligatoria para importar el archivo');
    }

    formData.append('fecha', datosCabecera.fecha);

    if (datosCabecera.numeroDocumento) {
      formData.append('numeroDocumento', datosCabecera.numeroDocumento);
    }

    return await lastValueFrom(
      this.http
        .post<any>(`${this.asientosBaseUrl}/import-flat-file`, formData)
        .pipe(map((response: any) => this.normalizeResponse(response))),
    );
  }

  private resolveTipoArchivo(file: File): 'CSV' | 'XLSX' | 'XLS' | 'TXT' {
    const extension = file.name.split('.').pop()?.toUpperCase() || '';

    switch (extension) {
      case 'TXT':
        return 'TXT';
      case 'CSV':
        return 'CSV';
      case 'XLSX':
        return 'XLSX';
      case 'XLS':
        return 'XLS';
      default:
        if (file.type === 'text/plain') {
          return 'TXT';
        }
        if (file.type === 'text/csv') {
          return 'CSV';
        }
        return 'XLSX';
    }
  }

  parseFlatFileResponse(
    response: ImportarAsientoResponseApi | null | undefined,
  ): ImportFlatFileResult {
    const content = this.resolveResponseContent(response);

    if (!content) {
      return { lineas: [], errores: [] };
    }

    if (Array.isArray(content)) {
      return { lineas: content, errores: [] };
    }

    const payload = content as ImportFlatFileContent;
    const lineas = Array.isArray(payload.lineas) ? payload.lineas : [];
    const errores = this.normalizeErrores(payload.errores);

    return { lineas, errores };
  }

  parseFlatFileResponseFromHttpError(error: unknown): ImportFlatFileResult {
    if (!error || typeof error !== 'object') {
      return { lineas: [], errores: [] };
    }

    const httpError = error as { error?: unknown };
    const apiError = httpError.error;

    if (!apiError || typeof apiError !== 'object') {
      return { lineas: [], errores: [] };
    }

    const record = apiError as Record<string, unknown>;
    return this.parseFlatFileResponse(
      (record['response'] as ImportarAsientoResponseApi | undefined) ??
        (apiError as ImportarAsientoResponseApi),
    );
  }

  private resolveResponseContent(response: unknown): unknown {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const record = response as Record<string, unknown>;

    if (record['content'] !== undefined) {
      return record['content'];
    }

    if (record['data'] && typeof record['data'] === 'object') {
      const data = record['data'] as Record<string, unknown>;
      if (data['content'] !== undefined) {
        return data['content'];
      }
    }

    return response;
  }

  private normalizeErrores(errores: unknown): string[] {
    if (!errores) {
      return [];
    }

    if (Array.isArray(errores)) {
      return errores
        .map((item) => {
          if (typeof item === 'string') {
            return item.trim();
          }
          if (item && typeof item === 'object' && 'message' in item) {
            return String((item as { message?: string }).message ?? '').trim();
          }
          return String(item).trim();
        })
        .filter(Boolean);
    }

    const mensaje = String(errores).trim();
    return mensaje ? [mensaje] : [];
  }

  private normalizeResponse(response: unknown): ImportarAsientoResponseApi {
    if (!response || typeof response !== 'object') {
      return (response ?? {}) as ImportarAsientoResponseApi;
    }

    const record = response as Record<string, unknown>;

    if (record['response'] && typeof record['response'] === 'object') {
      return record['response'] as ImportarAsientoResponseApi;
    }
    if (record['data']) {
      return record['data'] as ImportarAsientoResponseApi;
    }
    if (record['content']) {
      return record as unknown as ImportarAsientoResponseApi;
    }
    if (record['body']) {
      return record['body'] as ImportarAsientoResponseApi;
    }

    return record as unknown as ImportarAsientoResponseApi;
  }
}
